/**
 * UserService class provides methods for user authentication actions with shopware 5.
 *
 */
export class UserService {
    /**
     * A temporary method for logging in via the Shopware login page.
     * Asynchronously submits a sign-in form and dispatches events based on the response.
     * @param {HTMLFormElement} form - The sign-in form to be submitted.
     * @returns {Promise<boolean>} A Promise that resolves when the form submission is complete.
     */
    async formLogin(form: HTMLFormElement): Promise<boolean> {
        const { method, action } = form
        const body = new FormData(form)
        const { type } = await fetch(action, { method, body, redirect: 'manual' })

        /**
         * If the response is of the type "opaqueredirect", the shopware side is redirected
         * to the account page, and it can therefore be assumed that the login was successful.
         */
        const success = type === 'opaqueredirect'

        if (success) {
            this.dispatchEvent('userLoggedIn')
        } else {
            this.dispatchEvent('loginError')
        }

        return success
    }

    /**
     * Logs out the user and dispatches a 'userLoggedOut' event.
     * @returns {Promise<void>} A Promise that resolves when the logout request is complete.
     */
    async logout(): Promise<void> {
        await fetch('/account/logout', { method: 'HEAD' })
        this.dispatchEvent('userLoggedOut')
    }

    /**
     * Dispatches a custom event with the given type.
     * @param {string} type - The type of the event to be dispatched.
     */
    private dispatchEvent(type: string) {
        document.dispatchEvent(new CustomEvent(type))
    }
}
